import { useCallback, useEffect, useState } from "react";

import { Button } from "../components/Button";
import {
  generateAdminBonusDebitToken,
  generateAdminBonusToken,
  getAuthMe,
  getBonusBalance,
  getBonusHistory,
  getMyMarketReservations,
  getStoredAuthToken,
  pollAdminBonusRedeemEvents,
  redeemBonusToken,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import { useAuthUser, useIsTelegramStaff } from "../hooks/useTelegram";
import { BonusAdminCreditPage } from "./bonuses/BonusAdminCreditPage";
import { BonusAdminDebitPage } from "./bonuses/BonusAdminDebitPage";
import { BonusHistoryList } from "./bonuses/BonusHistoryList";
import { BonusReservationsList } from "./bonuses/BonusReservationsList";
import { BonusesActionCards } from "./bonuses/BonusesActionCards";
import { BonusesProfileCard } from "./bonuses/BonusesProfileCard";
import { QR_PREFIX, RECEIPT_BONUS_PERCENT } from "./bonuses/constants";
import {
  parseReceiptRub,
  previewBonusFromReceiptRub,
  showTelegramAlert,
} from "./bonuses/utils";

export function Bonuses({ onBack }) {
  const authUser = useAuthUser();
  const { setAuth } = useAuth();
  const isAdmin = useIsTelegramStaff();

  const [balance, setBalance] = useState(() => {
    const b = authUser?.bonus_balance;
    return typeof b === "number" && Number.isFinite(b) ? b : 0;
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [adminSubView, setAdminSubView] = useState("main");
  const [receiptRub, setReceiptRub] = useState("");
  const [deductAmount, setDeductAmount] = useState("");
  const [generatedToken, setGeneratedToken] = useState("");
  const [generatedBonusAmount, setGeneratedBonusAmount] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [reservations, setReservations] = useState([]);

  const refreshBalance = useCallback(() => {
    getBonusBalance()
      .then((data) => {
        const b = data?.balance;
        if (typeof b === "number" && Number.isFinite(b)) {
          setBalance(b);
        }
      })
      .catch(() => {});
  }, []);

  const refreshHistory = useCallback(() => {
    setHistoryLoading(true);
    getBonusHistory(30)
      .then((rows) => setHistory(Array.isArray(rows) ? rows : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    refreshBalance();
    refreshHistory();
    getMyMarketReservations()
      .then((rows) => setReservations(Array.isArray(rows) ? rows : []))
      .catch(() => setReservations([]));
  }, [refreshBalance, refreshHistory]);

  useEffect(() => {
    getAuthMe()
      .then((u) => {
        if (u?.id) setAuth(u, getStoredAuthToken());
      })
      .catch(() => {});
  }, [setAuth]);

  useEffect(() => {
    if (!isAdmin || !generatedToken) {
      return undefined;
    }
    const tick = () => {
      pollAdminBonusRedeemEvents(generatedToken)
        .then((data) => {
          const msg = data?.event?.message;
          if (msg) {
            showTelegramAlert(msg);
          }
        })
        .catch(() => {});
    };
    const id = window.setInterval(tick, 2000);
    tick();
    return () => window.clearInterval(id);
  }, [isAdmin, generatedToken]);

  const onGenerate = async () => {
    const rub = parseReceiptRub(receiptRub);
    if (rub == null) {
      setError("Введите сумму чека в рублях");
      return;
    }
    setGenerating(true);
    setError("");
    setSuccess("");
    setGeneratedToken("");
    setGeneratedBonusAmount(null);
    try {
      const data = await generateAdminBonusToken({ receipt_rub: rub });
      if (data?.token) {
        setGeneratedToken(data.token);
        if (typeof data.amount === "number") {
          setGeneratedBonusAmount(data.amount);
        }
      }
    } catch (err) {
      setError(err?.message || "Не удалось сгенерировать QR");
    } finally {
      setGenerating(false);
    }
  };

  const leaveAdminQrView = () => {
    setAdminSubView("main");
    setReceiptRub("");
    setDeductAmount("");
    setGeneratedToken("");
    setGeneratedBonusAmount(null);
    setError("");
    setSuccess("");
  };

  const onGenerateDebit = async () => {
    const amount = parseInt(String(deductAmount).trim(), 10);
    if (!Number.isFinite(amount) || amount < 1) {
      setError("Введите число бонусов не меньше 1");
      return;
    }
    setGenerating(true);
    setError("");
    setSuccess("");
    setGeneratedToken("");
    setGeneratedBonusAmount(null);
    try {
      const data = await generateAdminBonusDebitToken({ amount });
      if (data?.token) {
        setGeneratedToken(data.token);
        if (typeof data.amount === "number") {
          setGeneratedBonusAmount(data.amount);
        }
      }
    } catch (err) {
      setError(err?.message || "Не удалось сгенерировать QR");
    } finally {
      setGenerating(false);
    }
  };

  const receiptParsed = parseReceiptRub(receiptRub);
  const previewBonus =
    receiptParsed != null ? previewBonusFromReceiptRub(receiptParsed) : null;

  const deductParsed = parseInt(String(deductAmount).trim(), 10);
  const deductPreview =
    Number.isFinite(deductParsed) && deductParsed >= 1 ? deductParsed : null;

  const resetAdminQrForms = () => {
    setReceiptRub("");
    setDeductAmount("");
    setGeneratedToken("");
    setGeneratedBonusAmount(null);
    setError("");
    setSuccess("");
  };

  const onOpenAdd = () => {
    setAdminSubView("add");
    resetAdminQrForms();
  };

  const onOpenDeduct = () => {
    setAdminSubView("deduct");
    resetAdminQrForms();
  };

  const onScanQr = () => {
    setError("");
    setSuccess("");
    const tg = window.Telegram?.WebApp;
    if (!tg?.showScanQrPopup) {
      setError("QR-сканер доступен только в Telegram");
      return;
    }
    tg.showScanQrPopup({ text: "Наведите камеру на QR-код" }, (data) => {
      if (!data) return;
      tg.closeScanQrPopup();
      let token = data;
      if (token.startsWith(QR_PREFIX)) {
        token = token.slice(QR_PREFIX.length);
      }
      redeemBonusToken(token)
        .then((result) => {
          setSuccess(result?.message || "Бонусы начислены!");
          setBalance(result?.new_balance ?? balance);
          refreshHistory();
        })
        .catch((err) => {
          setError(err?.message || "Не удалось выполнить операцию по QR");
        });
    });
  };

  return (
    <section className="client">
      <header className="sectionHeader">
        <h2>
          {isAdmin && adminSubView === "add"
            ? "Добавить бонусы"
            : isAdmin && adminSubView === "deduct"
              ? "Списать бонусы"
              : "QR-коды и бонусы"}
        </h2>
        <p>
          {isAdmin && adminSubView === "add"
            ? `Сумма чека — гость получит ${RECEIPT_BONUS_PERCENT}% этой суммы в бонусах.`
            : isAdmin && adminSubView === "deduct"
              ? "Сколько списать — гость отсканирует QR, с его баланса снимется эта сумма."
              : "Профиль, баланс и действия с QR."}
        </p>
      </header>

      <div className="eventsToolbar">
        <Button
          onClick={
            isAdmin && (adminSubView === "add" || adminSubView === "deduct")
              ? leaveAdminQrView
              : onBack
          }
        >
          Назад
        </Button>
      </div>

      {isAdmin && adminSubView === "add" ? (
        <BonusAdminCreditPage
          receiptRub={receiptRub}
          onReceiptRubChange={setReceiptRub}
          previewBonus={previewBonus}
          generating={generating}
          error={error}
          generatedToken={generatedToken}
          generatedBonusAmount={generatedBonusAmount}
          onGenerate={onGenerate}
        />
      ) : null}

      {isAdmin && adminSubView === "deduct" ? (
        <BonusAdminDebitPage
          deductAmount={deductAmount}
          onDeductAmountChange={setDeductAmount}
          deductPreview={deductPreview}
          generating={generating}
          error={error}
          generatedToken={generatedToken}
          generatedBonusAmount={generatedBonusAmount}
          onGenerateDebit={onGenerateDebit}
        />
      ) : null}

      {!(isAdmin && (adminSubView === "add" || adminSubView === "deduct")) ? (
        <div className="bonusesPage">
          <BonusesProfileCard
            authUser={authUser}
            balance={balance}
            historyCount={history.length}
            reservationsCount={reservations.length}
          />

          <BonusReservationsList reservations={reservations} />

          {error ? <p className="errorMessage">{error}</p> : null}
          {success ? <p className="successMessage">{success}</p> : null}

          <BonusesActionCards
            isAdmin={isAdmin}
            onOpenAdd={onOpenAdd}
            onOpenDeduct={onOpenDeduct}
            onScanQr={onScanQr}
          />

          <BonusHistoryList history={history} historyLoading={historyLoading} />
        </div>
      ) : null}
    </section>
  );
}
